/* PayPal Smart Button — mirrors the Stripe upfront_50 flow.

   Loads PayPal SDK dynamically using client id + mode from /api/paypal/config.
   Creates order server-side (so the amount is trusted), then captures on
   approval and redirects to /payment/success with the PayPal order id.

   Renders `null` when PayPal is not configured, so the parent can also render
   its own fallback ("Coming soon" etc.) if needed.
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayPalCheckout({ projectId, currency = "usd", paymentType = "upfront_50", disabled, onError }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/paypal/config`);
        if (!cancelled) setConfig(res.data);
      } catch (_) {
        if (!cancelled) setConfig({ configured: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-4 text-xs text-zinc-500" data-testid="paypal-loading">
      <Loader2 size={14} className="animate-spin mr-2" /> Loading PayPal…
    </div>
  );
  if (!config?.configured) return null;

  const options = {
    clientId: config.client_id,
    currency: currency.toUpperCase() === "EUR" ? "EUR" : "USD",
    intent: "capture",
    "disable-funding": "credit,card",
    components: "buttons",
    ...(config.mode === "sandbox" ? { debug: false } : {}),
  };

  return (
    <div className="mt-4" data-testid="paypal-checkout">
      <div className="relative flex items-center justify-center mb-3">
        <span className="w-full border-t border-zinc-200" />
        <span className="absolute bg-white px-3 text-xs uppercase tracking-widest text-zinc-400">Or pay with PayPal</span>
      </div>
      <div className={disabled ? "pointer-events-none opacity-40" : ""}>
        <PayPalScriptProvider options={options}>
          <PayPalButtons
            style={{ layout: "horizontal", color: "gold", shape: "pill", label: "paypal", height: 45, tagline: false }}
            disabled={disabled}
            createOrder={async () => {
              const res = await axios.post(`${API}/paypal/orders`, {
                project_id: projectId, currency, payment_type: paymentType,
              });
              return res.data.order_id;
            }}
            onApprove={async (data) => {
              try {
                await axios.post(`${API}/paypal/orders/${data.orderID}/capture`);
                window.location.href = `/payment/success?paypal_order_id=${data.orderID}`;
              } catch (e) {
                if (onError) onError(e?.response?.data?.detail || "PayPal capture failed. Please try again.");
              }
            }}
            onError={(err) => {
              if (onError) onError((err && err.message) || "PayPal error. Please try again.");
            }}
          />
        </PayPalScriptProvider>
      </div>
      <div className="flex items-center justify-center gap-1 mt-3 text-[10px] text-zinc-400">
        <Shield size={10} />
        {config.mode === "sandbox" ? "PayPal sandbox — test mode" : "Secured by PayPal"}
      </div>
    </div>
  );
}
